// ============================================================================
// ZONE SERVICE
// ============================================================================
// Service layer for the Zone Master. getAll transforms backend ApiResponse<PagedResult<T>>
// into frontend PaginatedResponse<T> per the mandatory pattern in recsuit-web/CLAUDE.md.

import { get, post, put, del } from '../client/axiosClient';
import { zoneEndpoints, ZONE_BASE_PATH } from '../endpoints/zone.endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type {
  ApiResponse,
  PaginatedResponse,
  EmptyResponse,
  ID,
} from '../types';
import type {
  Zone,
  ZoneListItem,
  CreateZoneRequest,
  UpdateZoneRequest,
  ZoneSearchRequest,
} from '../types/zone.types';

class ZoneService {
  /**
   * Paginated list. Transforms backend PagedResult to frontend PaginatedResponse.
   * IMPORTANT: buildEndpointWithQuery is called with the RAW path (not the already-
   * prefixed endpoint) to avoid the double /v1 prefix.
   */
  async getAll(params?: ZoneSearchRequest): Promise<PaginatedResponse<ZoneListItem>> {
    const endpoint = params
      ? buildEndpointWithQuery(ZONE_BASE_PATH, params)
      : zoneEndpoints.list();

    const response = await get<any>(endpoint);
    const raw = response.data; // ApiResponse<PagedResult<ZoneDto>>
    const pagedResult = raw?.data ?? {};
    return {
      success: raw?.success ?? false,
      message: raw?.message,
      data: pagedResult.items ?? [],
      pagination: {
        currentPage: pagedResult.page ?? 1,
        pageSize: pagedResult.pageSize ?? 20,
        totalPages: pagedResult.totalPages ?? 0,
        totalItems: pagedResult.totalCount ?? 0,
        hasNextPage: pagedResult.hasNext ?? false,
        hasPreviousPage: pagedResult.hasPrevious ?? false,
      },
    };
  }

  async getById(id: ID): Promise<ApiResponse<Zone>> {
    const response = await get<ApiResponse<Zone>>(zoneEndpoints.getById(id));
    return response.data;
  }

  async getByCode(code: string): Promise<ApiResponse<Zone>> {
    const response = await get<ApiResponse<Zone>>(zoneEndpoints.getByCode(code));
    return response.data;
  }

  async create(data: CreateZoneRequest): Promise<ApiResponse<Zone>> {
    const response = await post<ApiResponse<Zone>>(zoneEndpoints.create(), data);
    return response.data;
  }

  async update(id: ID, data: UpdateZoneRequest): Promise<ApiResponse<Zone>> {
    const response = await put<ApiResponse<Zone>>(zoneEndpoints.update(id), data);
    return response.data;
  }

  async delete(id: ID): Promise<EmptyResponse> {
    const response = await del<EmptyResponse>(zoneEndpoints.delete(id));
    return response.data;
  }

  /** Returns TRUE when the given code is unique (available). */
  async validateCode(code: string, excludeId?: ID): Promise<ApiResponse<boolean>> {
    const endpoint = excludeId
      ? `${zoneEndpoints.validateCode(code)}?excludeId=${excludeId}`
      : zoneEndpoints.validateCode(code);
    const response = await get<ApiResponse<boolean>>(endpoint);
    return response.data;
  }
}

export const zoneService = new ZoneService();
export default zoneService;
